import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Paintbrush, X, Upload, Trash2,
  Image as ImageIcon, Palette,
  Search, Loader2, ImageOff, ChevronDown,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import GradientEditor, { buildGradientCSS } from './GradientEditor';

// ─── Constants ─────────────────────────────────────────────────────────────────

const PER_PAGE         = 50;
const DEFAULT_BG_QUERY = 'nature landscape';
const API_URL          = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
// Backend proxy — see MediaPanel. The provider credentials live on the server,
// never in this bundle.
const IMAGE_SEARCH_URL = `${API_URL}/api/search-images`;
const RECENT_KEY       = 'bgRecentImages_v2';
const MAX_RECENT       = 9;
const UPLOAD_BG_KEY    = 'bgUploads_v1';
const MAX_UPLOADS      = 8;
const MAX_BG_MB        = 10;
const BG_ACCEPTED      = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// ─── Solid color palette ───────────────────────────────────────────────────────

const solidColors = [
  '#ffffff','#f8f9fa','#e9ecef','#dee2e6','#ced4da','#adb5bd',
  '#6c757d','#495057','#343a40','#212529','#000000',
  '#fff5f5','#ffe3e3','#ffc9c9','#ffa8a8','#ff8787','#ff6b6b',
  '#fa5252','#f03e3e','#e03131','#c92a2a',
  '#fff0f6','#ffdeeb','#fcc2d7','#faa2c1','#f783ac','#f06595',
  '#e64980','#d6336c','#c2255c','#a61e4d',
  '#f8f0fc','#f3d9fa','#eebefa','#e599f7','#da77f2','#cc5de8',
  '#be4bdb','#ae3ec9','#9c36b5','#862e9c',
  '#f3f0ff','#e5dbff','#d0bfff','#b197fc','#9775fa','#845ef7',
  '#7950f2','#7048e8','#6741d9','#5f3dc4',
  '#edf2ff','#dbe4ff','#bac8ff','#91a7ff','#748ffc','#5c7cfa',
  '#4c6ef5','#4263eb','#3b5bdb','#364fc7',
  '#e7f5ff','#d0ebff','#a5d8ff','#74c0fc','#4dabf7','#339af0',
  '#228be6','#1c7ed6','#1971c2','#1864ab',
  '#e3fafc','#c5f6fa','#99e9f2','#66d9e8','#3bc9db','#22b8cf',
  '#15aabf','#1098ad','#0c8599','#0b7285',
  '#e6fcf5','#c3fae8','#96f2d7','#63e6be','#38d9a9','#20c997',
  '#12b886','#0ca678','#099268','#087f5b',
  '#ebfbee','#d3f9d8','#b2f2bb','#8ce99a','#69db7c','#51cf66',
  '#40c057','#37b24d','#2f9e44','#2b8a3e',
  '#f4fce3','#e9fac8','#d8f5a2','#c0eb75','#a9e34b','#94d82d',
  '#82c91e','#74b816','#66a80f','#5c940d',
  '#fff9db','#fff3bf','#ffec99','#ffe066','#ffd43b','#fcc419',
  '#fab005','#f59f00','#f08c00','#e67700',
  '#fff4e6','#ffe8cc','#ffd8a8','#ffc078','#ffa94d','#ff922b',
  '#fd7e14','#f76707','#e8590c','#d9480f',
];

// ─── Gradient presets stored as structured objects ─────────────────────────────
// This lets us load them directly into GradientEditor, and also render previews
// via buildGradientCSS() without any CSS string parsing.

const mkLinear = (angle, ...hexes) => ({
  type: 'linear',
  colors:    hexes,
  positions: hexes.map((_, i) => Math.round((i / (hexes.length - 1)) * 100)),
  opacities: hexes.map(() => 1),
  angle,
});
const mkRadial  = (...hexes) => ({ type:'radial',   colors:hexes, positions:hexes.map((_,i)=>Math.round(i/(hexes.length-1)*100)), opacities:hexes.map(()=>1), angle:90 });
const mkAngular = (angle, ...hexes) => ({ type:'angular',  colors:hexes, positions:hexes.map((_,i)=>Math.round(i/(hexes.length-1)*100)), opacities:hexes.map(()=>1), angle });

const gradientPresets = [
  { name:'Sunset',        obj: mkLinear(135, '#ff6b6b','#feca57') },
  { name:'Ocean',         obj: mkLinear(135, '#0077b6','#00b4d8') },
  { name:'Forest',        obj: mkLinear(135, '#2d6a4f','#95d5b2') },
  { name:'Purple Haze',   obj: mkLinear(135, '#667eea','#764ba2') },
  { name:'Fire',          obj: mkLinear(135, '#f12711','#f5af19') },
  { name:'Cotton Candy',  obj: mkLinear(135, '#ff9a9e','#fecfef') },
  { name:'Night Sky',     obj: mkLinear(135, '#0f0c29','#302b63','#24243e') },
  { name:'Mint',          obj: mkLinear(135, '#00b09b','#96c93d') },
  { name:'Peach',         obj: mkLinear(135, '#ed6ea0','#ec8c69') },
  { name:'Cool Blues',    obj: mkLinear(135, '#2193b0','#6dd5ed') },
  { name:'Royal',         obj: mkLinear(135, '#141e30','#243b55') },
  { name:'Fresh',         obj: mkLinear(135, '#11998e','#38ef7d') },
  { name:'Cosmic',        obj: mkLinear(135, '#c84b31','#2c394b') },
  { name:'Aurora',        obj: mkLinear(135, '#a8edea','#fed6e3') },
  { name:'Dusk',          obj: mkLinear(135, '#2c3e50','#fd746c') },
  { name:'Golden',        obj: mkLinear(135, '#f7971e','#ffd200') },
  { name:'Grape',         obj: mkLinear(135, '#DA22FF','#9733EE') },
  { name:'Twilight',      obj: mkLinear(135, '#1a1a2e','#16213e','#0f3460') },
  { name:'Blush',         obj: mkLinear(135, '#f6d365','#fda085') },
  { name:'Glacier',       obj: mkLinear(135, '#89f7fe','#66a6ff') },
  { name:'Mango',         obj: mkLinear(135, '#ffe259','#ffa751') },
  { name:'Steel',         obj: mkLinear(135, '#616161','#9bc5c3') },
  { name:'Candy',         obj: mkLinear(135, '#f953c6','#b91d73') },
  { name:'Neon',          obj: mkLinear(90,  '#f9f047','#0fd850') },
  { name:'Radial Glow',   obj: mkRadial('#ff6b6b','#764ba2') },
  { name:'Solar',         obj: mkRadial('#ffd200','#f7971e','#c92a2a') },
  { name:'Conic Rainbow', obj: mkAngular(0, '#ff0000','#ff8800','#ffff00','#00cc00','#0000ff','#8800cc','#ff0000') },
  { name:'Conic Sunset',  obj: mkAngular(0, '#f12711','#f5af19','#f12711') },
];

// ─── Custom gradient icon (diagonal fade square) ───────────────────────────────
// Represents a color gradient visually — distinct from the Layers panel icon.

const GradientIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="grad-tab-icon" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.12" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
      </linearGradient>
    </defs>
    <rect
      x="3" y="3" width="18" height="18" rx="3"
      fill="url(#grad-tab-icon)"
      stroke="currentColor" strokeWidth="2"
    />
  </svg>
);

// ─── Tiny shared UI pieces ─────────────────────────────────────────────────────

const TabBtn = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`
      flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium
      rounded-lg transition-all duration-200
      ${active
        ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-100'
        : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'}
    `}
  >
    {Icon && <Icon className="w-3 h-3 shrink-0" />}
    {label}
  </button>
);

const SectionLabel = ({ children, action }) => (
  <div className="flex items-center justify-between mb-2">
    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{children}</p>
    {action}
  </div>
);

const PillGroup = ({ options, value, onChange }) => (
  <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
    {options.map(({ label, val }) => (
      <button key={val} onClick={() => onChange(val)}
        className={`flex-1 py-1 text-xs font-medium rounded-md transition-all ${
          value === val ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >{label}</button>
    ))}
  </div>
);

const RangeSlider = ({ label, value, min, max, step=1, display, onChange }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-center">
      <span className="text-xs text-gray-600 font-medium">{label}</span>
      <span className="text-xs font-semibold text-gray-800 tabular-nums">{display ?? value}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-violet-600"
      style={{ backgroundImage:`linear-gradient(to right,#7c3aed ${((value-min)/(max-min))*100}%,#e5e7eb ${((value-min)/(max-min))*100}%)` }}
    />
  </div>
);

// ─── Image library sub-component ───────────────────────────────────────────────

const DEFAULT_GRADIENT_OBJ = {
  type: 'linear', colors: ['#667eea','#764ba2'],
  positions: [0, 100], opacities: [1, 1], angle: 135,
};

const BgImageLibrary = ({ onApplyImage, currentBgUrl = null, onUploadStart, onUploadEnd }) => {
  const { toast }           = useToast();
  const [query, setQuery]   = useState('');
  const [images, setImages] = useState([]);
  const [failedIds, setFailedIds] = useState(new Set());
  const [loading, setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage]       = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  const [curQuery, setCurQuery]   = useState(DEFAULT_BG_QUERY);
  const [noResults, setNoResults] = useState(false);
  const [applying, setApplying]   = useState(null);

  const [activeUrl,  setActiveUrl]  = useState(currentBgUrl);
  const [imgSize,    setImgSize]    = useState('cover');

  // Keep activeUrl in sync with the page's stored background so the thumbnail
  // never goes blank after switching pages or reopening the panel.
  useEffect(() => {
    setActiveUrl(currentBgUrl);
  }, [currentBgUrl]);
  const [imgPos,     setImgPos]     = useState('center center');
  const [imgOpacity, setImgOpacity] = useState(100);
  const [imgBlur,    setImgBlur]    = useState(0);

  const [recentImages, setRecentImages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, MAX_RECENT); }
    catch { return []; }
  });

  const scrollRef    = useRef(null);
  const sentinelRef  = useRef(null);
  const uploadBgRef  = useRef(null);

  const [isDraggingUpload, setIsDraggingUpload] = useState(false);
  const [uploadedBgs, setUploadedBgs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(UPLOAD_BG_KEY) || '[]').slice(0, MAX_UPLOADS); }
    catch { return []; }
  });

  const fetchImages = useCallback(async (q, pageNum, append=false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setNoResults(false);
    try {
      const params = new URLSearchParams({ q, per_page: PER_PAGE, page: pageNum });
      const res  = await fetch(`${IMAGE_SEARCH_URL}?${params}`);
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Image search failed (${res.status})`);
      }
      const data = await res.json();
      setTotalHits(data.totalHits || 0);
      if (!data.hits?.length) { if (!append) setNoResults(true); return; }
      // Already normalised by the proxy, whichever provider answered.
      const mapped = data.hits.map((h) => ({
        id:h.id,
        previewURL:h.previewURL,
        alt:h.alt||'photo',
        thumbW:h.thumbWidth||800, thumbH:h.thumbHeight||600,
      }));
      setImages((prev) => (append ? [...prev,...mapped] : mapped));
      setPage(pageNum);
    } catch (err) {
      toast({ title:'Failed to load images', description:err.message, variant:'destructive' });
      if (!append) setNoResults(true);
    } finally { setLoading(false); setLoadingMore(false); }
  }, [toast]);

  useEffect(() => { fetchImages(DEFAULT_BG_QUERY, 1, false); }, [fetchImages]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && !loadingMore && images.length < totalHits)
          fetchImages(curQuery, page+1, true);
      },
      { root: scrollRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, loadingMore, images.length, totalHits, curQuery, page, fetchImages]);

  const handleSearch = useCallback(() => {
    const q = query.trim() || DEFAULT_BG_QUERY;
    setCurQuery(q); setImages([]); setFailedIds(new Set());
    setPage(1); setTotalHits(0); fetchImages(q, 1, false);
  }, [query, fetchImages]);

  const reApply = useCallback((field, val) => {
    if (!activeUrl) return;
    onApplyImage(activeUrl, {
      size:    field==='size'    ? val : imgSize,
      position:field==='position'? val : imgPos,
      opacity: (field==='opacity'? val : imgOpacity)/100,
      blur:    field==='blur'   ? val : imgBlur,
    });
  }, [activeUrl, imgSize, imgPos, imgOpacity, imgBlur, onApplyImage]);

  const applyFull = useCallback((url, overrides={}) => {
    const cfg = {
      size:    overrides.size    ?? imgSize,
      position:overrides.position?? imgPos,
      opacity: (overrides.opacity!==undefined?overrides.opacity:imgOpacity)/100,
      blur:    overrides.blur    ?? imgBlur,
    };
    onApplyImage(url, cfg);
  }, [imgSize, imgPos, imgOpacity, imgBlur, onApplyImage]);

  const processUploadFile = useCallback((file) => {
    if (!BG_ACCEPTED.includes(file.type)) {
      toast({ title: 'Unsupported format', description: 'Use PNG, JPG, WebP or GIF.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_BG_MB * 1024 * 1024) {
      toast({ title: `Max ${MAX_BG_MB} MB allowed`, variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target.result;
      const entry = { id: `bgup-${Date.now()}`, name: file.name.replace(/\.\w+$/, ''), url };
      setUploadedBgs((prev) => {
        const updated = [entry, ...prev.filter(b => b.url !== url)].slice(0, MAX_UPLOADS);
        try { localStorage.setItem(UPLOAD_BG_KEY, JSON.stringify(updated)); } catch {}
        return updated;
      });
      setActiveUrl(url);
      applyFull(url);
      toast({ title: 'Background applied', description: 'Your image has been set as background.' });
    };
    reader.readAsDataURL(file);
  }, [applyFull, toast]);

  const handleApply = async (img) => {
    if (applying) return;
    setApplying(img.id);
    onUploadStart?.();

    // STRICT: background MUST be stored locally — never fall back to Pixabay URL
    try {
      const res = await fetch(`${API_URL}/api/upload-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: img.previewURL }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }

      const data = await res.json();
      if (!data.url) throw new Error('Server returned no URL');

      console.log('[ColorPanel] background stored locally:', data.url);
      setActiveUrl(data.url);
      setRecentImages((prev) => {
        const updated = [data.url, ...prev.filter((u) => u !== data.url)].slice(0, MAX_RECENT);
        try { localStorage.setItem(RECENT_KEY, JSON.stringify(updated)); } catch {}
        return updated;
      });
      applyFull(data.url);
      toast({ title: 'Background applied', description: 'Photo stored permanently.' });
    } catch (err) {
      console.error('[ColorPanel] background storage failed:', err.message);
      toast({
        title: 'Background upload failed',
        description: err.message || 'Could not store image. Please try again.',
        variant: 'destructive',
      });
      // IMPORTANT: do NOT call applyFull — no external URL enters the canvas
    } finally {
      onUploadEnd?.();
      setApplying(null);
    }
  };

  const handleRecentApply = (url) => {
    setActiveUrl(url); applyFull(url);
    toast({ title:'Background updated' });
  };

  const visible = images.filter((img) => !failedIds.has(img.id));
  const hasMore = images.length < totalHits;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search */}
      <div className="px-4 pb-3 pt-1 shrink-0">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input type="text" value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key==='Enter' && handleSearch()}
              placeholder="Search backgrounds…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 transition-all placeholder:text-gray-400"
            />
          </div>
          <button onClick={handleSearch} disabled={loading && !loadingMore}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm transition-colors disabled:opacity-50 flex items-center gap-1.5 shrink-0"
          >
            {loading && !loadingMore
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Search className="w-3.5 h-3.5" />}
            Search
          </button>
        </div>
        {totalHits > 0 && !loading && (
          <p className="text-[10px] text-gray-400 mt-1.5 truncate">
            {totalHits.toLocaleString()} photos · &ldquo;{curQuery}&rdquo;
          </p>
        )}
      </div>

      {/* Scrollable gallery */}
      <div className="flex-1 overflow-y-auto overscroll-y-contain min-h-0 px-4" ref={scrollRef}>

        {/* ── Upload background ─────────────────────────────────── */}
        <div className="mb-4 pt-1">
          <SectionLabel>Upload Background</SectionLabel>

          {/* Drop zone */}
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDraggingUpload(true); }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingUpload(false); }}
            onDrop={(e) => {
              e.preventDefault(); setIsDraggingUpload(false);
              const f = e.dataTransfer.files[0];
              if (f) processUploadFile(f);
            }}
            onClick={() => uploadBgRef.current?.click()}
            className={`
              relative flex flex-col items-center justify-center gap-2 rounded-xl border-2
              border-dashed py-5 cursor-pointer transition-all duration-200
              ${isDraggingUpload
                ? 'border-violet-500 bg-violet-50 scale-[0.98]'
                : 'border-gray-200 bg-gray-50 hover:border-violet-400 hover:bg-violet-50/40'}
            `}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDraggingUpload ? 'bg-violet-200' : 'bg-violet-100'}`}>
              <Upload className={`w-5 h-5 ${isDraggingUpload ? 'text-violet-600' : 'text-violet-500'}`} />
            </div>
            <div className="text-center">
              <p className={`text-xs font-semibold transition-colors ${isDraggingUpload ? 'text-violet-700' : 'text-gray-700'}`}>
                {isDraggingUpload ? 'Drop to set as background' : 'Upload background image'}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">PNG, JPG, WebP up to {MAX_BG_MB} MB</p>
            </div>
            {isDraggingUpload && (
              <div className="absolute inset-0 rounded-xl border-2 border-violet-500 animate-pulse pointer-events-none" />
            )}
            <input
              ref={uploadBgRef}
              type="file"
              accept={BG_ACCEPTED.join(',')}
              onChange={(e) => { if (e.target.files[0]) processUploadFile(e.target.files[0]); e.target.value = ''; }}
              className="hidden"
            />
          </div>

          {/* Uploaded backgrounds grid */}
          {uploadedBgs.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">My Uploads</p>
                <button
                  onClick={() => { setUploadedBgs([]); try { localStorage.removeItem(UPLOAD_BG_KEY); } catch {} }}
                  className="text-[10px] text-gray-400 hover:text-red-400 transition-colors"
                >Clear all</button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {uploadedBgs.map((bg) => (
                  <div
                    key={bg.id}
                    className={`group relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer aspect-square ${
                      activeUrl === bg.url
                        ? 'border-violet-500 ring-2 ring-violet-200'
                        : 'border-transparent hover:border-violet-400'
                    }`}
                    onClick={() => { setActiveUrl(bg.url); applyFull(bg.url); toast({ title: 'Background applied' }); }}
                  >
                    <img src={bg.url} alt={bg.name} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                    {activeUrl === bg.url && (
                      <div className="absolute top-1 left-1 bg-violet-600 text-white text-[8px] font-bold px-1 py-0.5 rounded-md">Active</div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedBgs((prev) => {
                          const updated = prev.filter(b => b.id !== bg.id);
                          try { localStorage.setItem(UPLOAD_BG_KEY, JSON.stringify(updated)); } catch {}
                          return updated;
                        });
                      }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/90 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Divider before Pixabay section */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 h-px bg-gray-100" />
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest shrink-0">Pixabay Photos</p>
          <div className="flex-1 h-px bg-gray-100" />
        </div>

        {/* Active controls */}
        {activeUrl && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <SectionLabel>Active Background</SectionLabel>
              <button onClick={() => { setActiveUrl(null); onApplyImage(null,null); }}
                className="text-[10px] text-gray-400 hover:text-red-400 transition-colors">Clear</button>
            </div>
            <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm mb-3" style={{aspectRatio:'16/9'}}>
              <img src={activeUrl} alt="bg" className="w-full h-full object-cover"/>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50/80 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100 bg-white">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Controls</p>
              </div>
              <div className="p-3 space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">Fit</p>
                  <PillGroup
                    options={[{label:'Cover',val:'cover'},{label:'Contain',val:'contain'},{label:'Original',val:'auto'}]}
                    value={imgSize}
                    onChange={(v) => { setImgSize(v); reApply('size',v); }}
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">Position</p>
                  <div className="grid grid-cols-5 gap-1">
                    {[{l:'●',v:'center center',t:'Center'},{l:'↑',v:'center top',t:'Top'},
                      {l:'↓',v:'center bottom',t:'Bottom'},{l:'←',v:'left center',t:'Left'},{l:'→',v:'right center',t:'Right'}]
                      .map(({l,v,t}) => (
                        <button key={v} title={t}
                          onClick={() => { setImgPos(v); reApply('position',v); }}
                          className={`h-7 rounded-md text-sm font-bold transition-all border ${
                            imgPos===v ? 'bg-violet-600 text-white border-violet-600'
                              : 'bg-white text-gray-500 border-gray-200 hover:border-violet-300 hover:text-violet-600'
                          }`}
                        >{l}</button>
                      ))}
                  </div>
                </div>
                <RangeSlider label="Opacity" value={imgOpacity} min={0} max={100} display={`${imgOpacity}%`}
                  onChange={(v) => { setImgOpacity(v); reApply('opacity',v); }} />
                <RangeSlider label="Blur" value={imgBlur} min={0} max={40} display={`${imgBlur}px`}
                  onChange={(v) => { setImgBlur(v); reApply('blur',v); }} />
              </div>
            </div>
          </div>
        )}

        {/* Recents */}
        {recentImages.length > 0 && (
          <div className="mb-4">
            <SectionLabel action={
              <button onClick={() => { setRecentImages([]); try{localStorage.removeItem(RECENT_KEY);}catch{} }}
                className="text-[10px] text-gray-400 hover:text-red-400 transition-colors">Clear</button>
            }>Recent</SectionLabel>
            <div className="grid grid-cols-3 gap-1.5">
              {recentImages.map((url,i) => (
                <button key={i} onClick={() => handleRecentApply(url)}
                  className={`relative rounded-xl overflow-hidden border-2 transition-all hover:shadow-md ${
                    activeUrl===url ? 'border-violet-500 ring-2 ring-violet-200' : 'border-transparent hover:border-violet-300'
                  }`} style={{aspectRatio:'1'}}>
                  <img src={url} alt="" className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.parentElement.style.display='none'; }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Skeleton */}
        {loading && images.length === 0 && (
          <div className="grid grid-cols-2 gap-2">
            {Array.from({length:12}).map((_,i) => (
              <div key={i} className="rounded-xl bg-gray-100 animate-pulse"
                style={{height:[120,100,140,110][i%4]}} />
            ))}
          </div>
        )}

        {/* No results */}
        {!loading && noResults && (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
              <ImageOff className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No photos found</p>
            <button onClick={() => { setQuery(''); setCurQuery(DEFAULT_BG_QUERY); setImages([]); fetchImages(DEFAULT_BG_QUERY,1,false); }}
              className="text-xs text-violet-600 hover:text-violet-700 font-medium">
              Reset search
            </button>
          </div>
        )}

        {/* Grid */}
        {visible.length > 0 && (
          <>
            {!activeUrl && <SectionLabel>Click photo to set as background</SectionLabel>}
            <div className="grid grid-cols-2 gap-2">
              {visible.map((img, idx) => {
                const isApplying = applying === img.id;
                const ratio = img.thumbW && img.thumbH
                  ? `${img.thumbW}/${img.thumbH}` : ['4/3','1/1','3/4','16/9'][idx%4];
                return (
                  <button key={`${img.id}-${idx}`}
                    onClick={() => !isApplying && handleApply(img)} disabled={isApplying}
                    className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 bg-gray-100 disabled:cursor-wait
                      ${activeUrl===img.previewURL ? 'border-violet-500 ring-2 ring-violet-200'
                        : 'border-transparent hover:border-violet-400'}`}
                    style={{aspectRatio:ratio}} title={img.alt}
                  >
                    <img src={img.previewURL} alt={img.alt} loading="lazy" decoding="async"
                      onError={() => setFailedIds((p) => new Set([...p,img.id]))}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end p-2">
                      <p className="text-white text-[10px] leading-tight line-clamp-2">{img.alt}</p>
                    </div>
                    {activeUrl===img.previewURL && (
                      <div className="absolute top-1.5 left-1.5 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">Active</div>
                    )}
                    {isApplying && (
                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                      </div>
                    )}
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-gray-700 text-xs font-bold">+</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div ref={sentinelRef} className="h-6 mt-1" aria-hidden />
            {loadingMore && (
              <div className="flex items-center justify-center py-5 gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                <span className="text-xs text-gray-500">Loading more…</span>
              </div>
            )}
            {hasMore && !loadingMore && (
              <div className="flex justify-center py-3">
                <button onClick={() => fetchImages(curQuery, page+1, true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-200 text-xs text-gray-500 hover:border-violet-300 hover:text-violet-600 transition-colors">
                  <ChevronDown className="w-3.5 h-3.5" /> Load more
                </button>
              </div>
            )}
            {!hasMore && images.length > 0 && (
              <p className="text-center text-[10px] text-gray-400 py-3">
                All {totalHits.toLocaleString()} photos loaded
              </p>
            )}
          </>
        )}
      </div>

      {/* Attribution */}
      <div className="shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50/60">
        <p className="text-[10px] text-gray-400 text-center">
          Photos by{' '}
          <a href="https://pixabay.com" target="_blank" rel="noopener noreferrer"
            className="text-violet-500 hover:underline font-medium">Pixabay</a>
        </p>
      </div>
    </div>
  );
};

// ─── Main ColorPanel ───────────────────────────────────────────────────────────

const ColorPanel = ({ onSetBackground, backgroundConfig, onClose, onUploadStart, onUploadEnd }) => {
  const { toast } = useToast();

  // Tabs: 'color' | 'gradient' | 'image'
  const [tab, setTab] = useState('color');

  // ── Color tab
  const [customColor,  setCustomColor]  = useState('#ffffff');
  const [pendingColor, setPendingColor] = useState('#ffffff');

  // ── Gradient tab — structured object that GradientEditor understands
  const [gradientObj, setGradientObj] = useState(DEFAULT_GRADIENT_OBJ);

  // Sync incoming backgroundConfig
  useEffect(() => {
    if (!backgroundConfig) return;
    if (backgroundConfig.type === 'image')    setTab('image');
    else if (backgroundConfig.type === 'gradient') setTab('gradient');
    else {
      setTab('color');
      const c = backgroundConfig.color || '#ffffff';
      setCustomColor(c); setPendingColor(c);
    }
  }, [backgroundConfig]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const applyColor = (color) => {
    onSetBackground('color', color);
    toast({ title:'Background updated', description:'Color applied' });
  };

  const applyGradient = useCallback(() => {
    onSetBackground('gradient', buildGradientCSS(gradientObj));
    toast({ title:'Background updated', description:'Gradient applied' });
  }, [gradientObj, onSetBackground, toast]);

  // Load a preset into the editor and immediately apply it
  const loadPreset = (preset) => {
    setGradientObj(preset.obj);
    onSetBackground('gradient', buildGradientCSS(preset.obj));
    toast({ title:`"${preset.name}" applied` });
  };

  // Image handler — called from BgImageLibrary
  const handleApplyImage = useCallback((url, config) => {
    if (!url) { onSetBackground('color', '#ffffff'); return; }
    onSetBackground('image', url, config);
  }, [onSetBackground]);

  // Current image URL from the page's stored backgroundConfig — passed to BgImageLibrary
  // so its thumbnail stays correct after page switches.
  const currentBgUrl = backgroundConfig?.type === 'image' ? (backgroundConfig?.url ?? null) : null;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-white select-none">

      {/* ── Fixed header ─────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-sm">
              <Palette className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm leading-tight">Page Background</h3>
              <p className="text-[10px] text-gray-400 leading-tight">Current page only</p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose}
              className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Pill tabs: Color | Gradient | Image */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          <TabBtn active={tab==='color'}    onClick={()=>setTab('color')}    icon={Palette}   label="Color"    />
          <TabBtn active={tab==='gradient'} onClick={()=>setTab('gradient')} icon={GradientIcon} label="Gradient" />
          <TabBtn active={tab==='image'}    onClick={()=>setTab('image')}    icon={ImageIcon} label="Image"    />
        </div>
      </div>

      {/* ── Scrollable body ─────────────────────────────────────────
           overscroll-y-contain stops scroll from propagating to the
           canvas or the main page when the user reaches the top/bottom.
      ─────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overscroll-y-contain min-h-0">

        {/* ════════════ COLOR TAB ════════════ */}
        {tab === 'color' && (
          <div className="p-4 space-y-5 pb-6">
            {/* Custom picker row */}
            <div>
              <SectionLabel>Custom Color</SectionLabel>
              <div className="flex items-center gap-2">
                <label className="shrink-0 cursor-pointer">
                  <div className="w-9 h-9 rounded-lg border-2 border-white shadow ring-1 ring-gray-200 transition-transform hover:scale-105"
                    style={{ backgroundColor: pendingColor }} />
                  <input type="color" value={pendingColor}
                    onChange={(e) => { setPendingColor(e.target.value); setCustomColor(e.target.value); }}
                    className="sr-only" />
                </label>
                <input type="text" value={customColor}
                  onChange={(e) => { setCustomColor(e.target.value); if(/^#[0-9a-fA-F]{6}$/.test(e.target.value)) setPendingColor(e.target.value); }}
                  onKeyDown={(e) => e.key==='Enter' && applyColor(customColor)}
                  className="w-28 h-9 px-2.5 text-sm font-mono rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 transition-all"
                  placeholder="#ffffff" spellCheck={false} />
                <button onClick={() => applyColor(customColor)}
                  className="flex-1 h-9 rounded-lg bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white text-xs font-semibold tracking-wide transition-colors shadow-sm">
                  Apply
                </button>
              </div>
            </div>

            {/* Palette */}
            <div>
              <SectionLabel>Palette</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {solidColors.map((color) => (
                  <button key={color}
                    onClick={() => { setPendingColor(color); setCustomColor(color); applyColor(color); }}
                    className={`w-7 h-7 rounded-lg border transition-all duration-150 hover:scale-110 hover:shadow-md hover:z-10 relative
                      ${customColor===color ? 'border-violet-500 ring-2 ring-violet-300 scale-110 z-10' : 'border-gray-200 hover:border-gray-300'}`}
                    style={{ backgroundColor:color }} title={color}
                    data-testid={`color-${color.replace('#','')}`} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════════════ GRADIENT TAB ════════════ */}
        {tab === 'gradient' && (
          <div className="p-4 space-y-5 pb-6">

            {/* ── 1. Custom Editor ──────────────────────────────────── */}
            <div>
              <SectionLabel>Custom Editor</SectionLabel>
              <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                <GradientEditor value={gradientObj} onChange={setGradientObj} />
              </div>
            </div>

            {/* ── 2. Apply button (inline, above presets) ───────────── */}
            <button onClick={applyGradient}
              className="w-full h-10 rounded-xl bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white text-sm font-semibold tracking-wide transition-colors shadow-sm flex items-center justify-center gap-2">
              <Paintbrush className="w-4 h-4" /> Apply Gradient
            </button>

            {/* ── 3. Presets — 2-col cards ──────────────────────────── */}
            <div>
              <SectionLabel>Presets</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                {gradientPresets.map((p) => (
                  <button key={p.name}
                    onClick={() => loadPreset(p)}
                    className={`relative rounded-xl border-2 overflow-hidden transition-all duration-200 hover:shadow-md group
                      ${buildGradientCSS(gradientObj)===buildGradientCSS(p.obj)
                        ? 'border-violet-500 ring-2 ring-violet-200'
                        : 'border-transparent hover:border-violet-400'}`}
                    style={{ background:buildGradientCSS(p.obj), height:68 }}
                    title={p.name}
                    data-testid={`gradient-${p.name.toLowerCase().replace(/ /g,'-')}`}
                  >
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors flex items-end p-1.5">
                      <span className="text-white text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">
                        {p.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════════════ IMAGE TAB ════════════ */}
        {tab === 'image' && (
          <div className="flex flex-col min-h-0 pt-3" style={{ height:'100%' }}>
            <BgImageLibrary onApplyImage={handleApplyImage} currentBgUrl={currentBgUrl} onUploadStart={onUploadStart} onUploadEnd={onUploadEnd} />
          </div>
        )}
      </div>

      {/* ── Footer note (color tab only) ──────────────────────────── */}
      {tab === 'color' && (
        <div className="shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50/60">
          <p className="text-[10px] text-gray-400 text-center">
            Changes apply to the current page only
          </p>
        </div>
      )}
    </div>
  );
};

export default ColorPanel;
